import { Injectable } from '@angular/core';
import { CanActivate, Route, Router, UrlTree } from '@angular/router';

import { MainLayoutComponent } from './layout/app-layout/main-layout/main-layout.component';
import { AuthLayoutComponent } from './layout/app-layout/auth-layout/auth-layout.component';

import { AuthGuard } from '@core/guard/auth.guard';
import { ClearStorageGuard } from '@core/guard/clear-storage.guard';
import { twoFaGuard } from '@core/guard/two-fa.guard';
import { roleGuard } from '@core/guard/role.guard';

import { AuthManagementService } from '@core/service/auth-management.service';
import { UserRole } from '@core/models/Enums/UserRole';

import { Page404Component } from './authentication/page404/page404.component';
import { ForgotPasswordComponent } from './authentication/forgot-password/forgot-password.component';
import { Page500Component } from './authentication/page500/page500.component';
import { SigninComponent } from './authentication/signin/signin.component';
import { ResetPasswordComponent } from './authentication/reset-password/reset-password.component';
import { ConfirmPasswordComponent } from './authentication/confirm-password/confirm-password.component';
import { RecuperarPinComponent } from './authentication/recuperar-pin/recuperar-pin.component';

import { DashboardComponent } from './pages/dashboard/dashboard.component';

import { UsersComponent } from './pages/admin/users/users.component';
import { UserEditCreateComponent } from './pages/admin/users/user-edit-create/user-edit-create.component';
import { UserProfileComponent } from './pages/admin/users/user-profile/user-profile.component';

import { ProfileComponent } from './pages/user/profile/profile.component';

import { BranchListComponent } from './pages/branch/branch-list/branch-list.component';
import { BranchEditCreateComponent } from './pages/branch/branch-list/branch-edit-create/branch-edit-create.component';
import { BranchProfileComponent } from './pages/branch/branch-list/branch-profile/branch-profile.component';

import { PatientListComponent } from './pages/patient/patient-list/patient-list.component';
import { PatientCreateEditComponent } from './pages/patient/patient-list/patient-create-edit/patient-create-edit.component';
import { PatientProfileComponent } from './pages/patient/patient-list/patient-profile/patient-profile.component';
import { PatientHealthEventListComponent } from './pages/patient/patient-health-event-list/patient-health-event-list.component';

import { PrescriptionListComponent } from './pages/prescription/prescription-list/prescription-list.component';
import { PrescriptionEditCreateComponent } from './pages/prescription/prescription-list/prescription-edit-create/prescription-edit-create.component';
import { PrescriptionDetailsComponent } from './pages/prescription/prescription-list/prescription-details/prescription-details.component';
import { PrescriptionSupplyComponent } from './pages/prescription/prescription-supply/prescription-supply.component';

import { DoctorListComponent } from './pages/doctor/doctor-list/doctor-list.component';
import { DoctorEditCreateComponent } from './pages/doctor/doctor-list/doctor-edit-create/doctor-edit-create.component';
import { DoctorProfileComponent } from './pages/doctor/doctor-list/doctor-profile/doctor-profile.component';
import { RegistrationRequestListComponent } from './pages/doctor/registration-request-list/registration-request-list.component';
import { PublicDoctorRegistrationComponent } from './pages/registration/public-doctor-registration/public-doctor-registration.component';
import { PublicPatientRegistrationComponent } from './pages/registration/public-patient-registration/public-patient-registration.component';
import { RegistrationStatusComponent } from './pages/registration/registration-status/registration-status.component';

import { CatalogsComponent } from './pages/catalogs/catalogs-list/catalogs.component';
import { ReportsComponent } from './pages/reports/reports-list/reports.component';
import { CatalogsV2Component } from './pages/catalogs/catalogs-list-v2/catalogs-v2.component';

import { MyPrescriptionMedsComponent } from './pages/patient/patient-list/patient-my-prescriptions/my-prescription-meds/my-prescription-meds.component';
import { RedirectByRoleComponent } from "@core/components/redirect-by-role/redirect-by-role.component";
import {regionGuard} from "@core/guard/region.guard";
import {
  PrescriptionEditComponent
} from "./pages/prescription/prescription-list/prescription-edit-only/prescription-edit.component";

@Injectable({ providedIn: 'root' })
export class SkipAuthGuard implements CanActivate {
  constructor(
    private auth: AuthManagementService,
    private router: Router
  ) {}

  canActivate(): boolean | UrlTree {
    return this.auth.isAuthenticated()
      ? this.router.createUrlTree([this.auth.getDefaultRoute()])
      : true;
  }
}

export const APP_ROUTE: Route[] = [
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard, twoFaGuard],
    canActivateChild: [twoFaGuard],
    children: [
      { path: '', component: RedirectByRoleComponent, pathMatch: 'full' },

      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [roleGuard],
        data: {
          roles: [UserRole.Paciente]
        }
      },

      {
        path: 'admin',
        children: [
          {
            path: 'user-list',
            component: UsersComponent,
            canActivate: [regionGuard, roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ],
              excludeRegions: ['ES']
            }
          },
          {
            path: 'create-edit',
            component: UserEditCreateComponent,
            canActivate: [regionGuard, roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ],
              excludeRegions: ['ES']
            }
          },
          {
            path: 'create-edit/:param/:gid',
            component: UserEditCreateComponent,
            canActivate: [regionGuard, roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ],
              excludeRegions: ['ES']
            }
          },
          {
            path: 'profile/:param/:gid',
            component: UserProfileComponent,
            canActivate: [regionGuard, roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ],
              excludeRegions: ['ES']
            }
          },
        ],
      },

      {
        path: 'branch',
        children: [
          {
            path: 'branch-list',
            component: BranchListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Particular
              ]
            }
          },
          {
            path: 'create-edit/:gid',
            component: BranchEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Particular
              ]
            }
          },
          {
            path: 'create-edit/:gid/:param',
            component: BranchEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Particular
              ]
            }
          },
          {
            path: 'profile/:gid/:param',
            component: BranchProfileComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Particular
              ]
            }
          },
        ],
      },

      {
        path: 'doctor',
        children: [
          {
            path: 'doctor-list',
            component: DoctorListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ]
            }
          },

          // aquí sí dejo create/profile con el mismo bloque
          // porque tu sidebar sugiere que esos roles administran médicos
          {
            path: 'create-edit',
            component: DoctorEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ]
            }
          },
          {
            path: 'create-edit/:param',
            component: DoctorEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ]
            }
          },
          {
            path: 'profile/:param/:param2',
            component: DoctorProfileComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ]
            }
          },
          {
            path: 'registration-requests',
            component: RegistrationRequestListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [
                UserRole.Admin,
                UserRole.Supervisor_Sucursales,
                UserRole.Responsable_Farmacia
              ]
            }
          },
        ],
      },

      {
        path: 'patient',
        children: [
          {
            path: 'patient-list',
            component: PatientListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'create-edit',
            component: PatientCreateEditComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'create-edit/:param',
            component: PatientCreateEditComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'profile/:param',
            component: PatientProfileComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'health-event-list',
            component: PatientHealthEventListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Paciente]
            }
          },
        ],
      },

      {
        path: 'catalogs',
        children: [
          // si luego reactivas catalogs-list, aquí mismo le pones Admin
          {
            path: 'catalogs-list',
            component: CatalogsComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin]
            }
          },
          {
            path: 'catalogs-list-v2',
            component: CatalogsV2Component,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin]
            }
          },
        ],
      },

      {
        path: 'reports',
        children: [
          {
            path: 'reports-list',
            component: ReportsComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin]
            }
          }
        ],
      },

      {
        path: 'prescription',
        children: [
          {
            path: 'prescription-list',
            component: PrescriptionListComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin, UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'details/:param/:par/:stat',
            component: PrescriptionDetailsComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin, UserRole.Medico, UserRole.Particular, UserRole.Paciente]
            }
          },
          {
            path: 'meds/:param/:par/:par2/:par3/:par4/:par5',
            component: MyPrescriptionMedsComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin, UserRole.Medico, UserRole.Particular, UserRole.Paciente]
            }
          },
          {
            path: 'prescription-supply',
            component: PrescriptionSupplyComponent,
            canActivate: [regionGuard, roleGuard],
            data: {
              roles: [UserRole.Empleado_Farmacia, UserRole.Responsable_Farmacia],
              excludeRegions: ['ES']
            }
          },
          {
            path: 'tutorial-create',
            component: PrescriptionEditComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'tutorial-edit',
            component: PrescriptionEditComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'create',
            component: PrescriptionEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin, UserRole.Medico, UserRole.Particular]
            }
          },
          {
            path: 'edit/:idReceta/:idPaciente',
            component: PrescriptionEditCreateComponent,
            canActivate: [roleGuard],
            data: {
              roles: [UserRole.Admin, UserRole.Medico, UserRole.Particular]
            }
          },
        ],
      },

      {
        path: 'user',
        children: [
          { path: 'profile', component: ProfileComponent }
        ],
      },
    ],
  },

  {
    path: 'registro',
    component: AuthLayoutComponent,
    children: [
      { path: 'paciente', component: PublicPatientRegistrationComponent },
      { path: 'medico', component: PublicDoctorRegistrationComponent },
      { path: 'estatus', component: RegistrationStatusComponent },
    ],
  },

  {
    path: 'authentication',
    component: AuthLayoutComponent,
    canActivate: [SkipAuthGuard],
    children: [
      { path: '', redirectTo: 'signin', pathMatch: 'full' },
      { path: 'signin', component: SigninComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'verifyemail/:token', component: ConfirmPasswordComponent },
      { path: 'reset/:token', component: ResetPasswordComponent },
      { path: 'page404', component: Page404Component },
      { path: 'page500', component: Page500Component },
    ],
  },

  {
    path: 'Recuperar',
    children: [
      { path: 'Pin', component: RecuperarPinComponent },
    ],
  },

  { path: '**', component: Page404Component },
];
